import _ from "lodash";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { WSErrorContainer } from "../error/WSErrorContainer";
import { getLocStore } from "../localization";
import { LocContext } from "../localization/LocContext";
import { useEvaluatedLocale } from "../localization/useEvaluatedLocale";
import { CvpSettings } from "../settings/CvpSettings";
import { useSettings } from "../settings/useSettings";
import { IWsRoutePath, PathType, path, wsRouteMatch } from "../url/WSRouter";
import { useCurrentPath } from "../url/useCurrentPath";
import { useWhoAmI } from "../user/useWhoAmI";

// Every page here should have <PageLayout>
const FrontPage = React.lazy(() => import("../app/pages/FrontPage"));
const Messages = React.lazy(() => import("../app/pages/Messages"));
const Login = React.lazy(() => import("../app/pages/Login"));
const NothingHere = React.lazy(() => import("../app/pages/NothingHere"));
const AttachProfile = React.lazy(() => import("../app/pages/AttachProfile"));
const SelectProfile = React.lazy(() => import("../app/pages/SelectProfile"));
const ColorTest = React.lazy(() => import("../app/pages/ColorTest"));
const TextTest = React.lazy(() => import("../app/pages/TextTest"));
const EmbedStats = React.lazy(() => import("../app/pages/EmbedStats"));
const UserOverview = React.lazy(() => import("../app/pages/UserOverview"));
const WorkSchedule = React.lazy(() => import("../app/pages/WorkSchedule"));
const Jobs = React.lazy(() => import("../app/pages/Jobs"));
const Apply = React.lazy(() => import("../app/pages/Apply"));
const ApplyManually = React.lazy(() => import("../app/pages/ApplyManually"));
const Questionnaire = React.lazy(() => import("../app/pages/Questionnaire"));
const Job = React.lazy(() => import("../app/pages/Job"));
const AdminSettingsEditor = React.lazy(
  () => import("../app/pages/AdminSettingsEditor")
);
const CandidateImpersonation = React.lazy(
  () => import("../app/pages/CandidateImpersonation")
);
const ProfilePage = React.lazy(() => import("../app/pages/Profile"));
const ErrorTest = React.lazy(() => import("../app/pages/ErrorTest"));
const HowToDeleteMyData = React.lazy(
  () => import("../app/pages/HowToDeleteMyData")
);
const PrivacyStatement = React.lazy(
  () => import("../app/pages/PrivacyStatement")
);
const CreateProfilePage = React.lazy(
  () => import("../app/pages/CreateProfile")
);
const ApplicationSent = React.lazy(
  () => import("../app/pages/ApplicationSent")
);
const AccountCreatedNoConern = React.lazy(
  () => import("../app/pages/CreationSuccessful")
);
const TimeSheetPage = React.lazy(() => import("../app/pages/TimeSheet"));
const TimeSheetsOverviewPage = React.lazy(
  () => import("../app/pages/TimeSheetsOverview")
);

const ParameterOverride = React.lazy(
  () => import("../app/pages/ParameterOverride")
);

const PwaActivation = React.lazy(() => import("../app/pages/PwaActivation"));

export const Routes = observer(() => {
  const result = useEvaluateRoutes(allRoutes);
  const fallbackComponent = useFallbackComponent(result);

  const evaluatedLocale = useEvaluatedLocale();

  const contexts = _.uniq(
    result
      .filter((r) => !!r.match && !fallbackComponent && r.route.Context)
      .map((r) => r.route.Context)
  );

  if (contexts.length > 1) {
    throw new Error("Only one context allowed");
  }

  let Context = contexts[0] ?? FallbackContext;

  return (
    <Context>
      {fallbackComponent}
      {result.map((r, index) => {
        const Component = r.route.Component;

        const preloadLocale = r.match ? r.route.preloadLocale ?? [] : [];

        if (r.match) {
          preloadLocale.push(LocContext.GENERAL);
          preloadLocale.push(LocContext.PRIME);
          preloadLocale.push(LocContext.ERROR);
          preloadLocale.push(LocContext.MESSAGES);
        }

        const locStores = preloadLocale.map((locContext) =>
          getLocStore(locContext)
        );

        const allPreloadsDone = locStores.every(
          (locStore) =>
            locStore.gotTranslationsForLocale === evaluatedLocale &&
            locStore.overrides?.locale === evaluatedLocale
        );

        return (
          <React.Fragment key={index}>
            {!!r.match && !fallbackComponent && (
              <React.Suspense fallback={null}>
                <WSErrorContainer>
                  <RouteContainer
                    style={{
                      opacity: allPreloadsDone ? 1 : 0,
                      flexGrow: 1,
                    }}
                  >
                    <Component />
                  </RouteContainer>
                </WSErrorContainer>
              </React.Suspense>
            )}
          </React.Fragment>
        );
      })}
    </Context>
  );
});

const FallbackContext = ({ children }) => <>{children}</>;

const RouteContainer = styled.div`
  display: flex;
  flex-direction: column;

  > .ws-error-message {
    margin: 1rem;
  }
`;

const useEvaluateRoutes = (routes: Route[]) => {
  const { confirmedNotLoggedIn, confirmedNoProfileInConcern } = useWhoAmI();
  const currentPath = useCurrentPath();
  const settings = useSettings();

  const result: Result[] = routes.map((route, index) => {
    const match = wsRouteMatch(route.path, currentPath);

    const missingConcern = route.requiresConcern && !window.cvpIdConcern;

    let missingLogin =
      route.requiresVismaConnectLoggedIn && confirmedNotLoggedIn;

    const missingProfileInConcern =
      !missingConcern &&
      !missingLogin &&
      confirmedNoProfileInConcern &&
      route.requiresProfileInConcern;

    return {
      activated: route.activated?.(settings) ?? true,
      route,
      match,
      missingConcern,
      missingLogin,
      missingProfileInConcern,
    };
  });

  return result;
};

export const allRoutes: Route[] = [
  {
    path: path("frontpage", PathType.EXACT_OR_SUBPATH),
    key: "frontpage",
    Component: () => <FrontPage />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.GENERAL, LocContext.JOBS],
  },
  {
    path: path("messages", PathType.EXACT_OR_SUBPATH),
    key: "messages",
    Component: () => <Messages />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: true,
    requiresProfileInConcern: true,
    preloadLocale: [LocContext.MESSAGES],
    activated: (settings) => !!settings.messages_enabled,
  },
  {
    path: path("workschedule", PathType.EXACT_OR_SUBPATH),
    key: "workschedule",
    Component: () => <WorkSchedule />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: true,
    requiresProfileInConcern: true,
    preloadLocale: [LocContext.WORKSCHEDULE],
    activated: (settings) => !!settings.workschedule_enabled,
  },
  {
    path: path("questionnaire", PathType.EXACT_OR_SUBPATH),
    key: "questionnaire",
    Component: () => <Questionnaire />,

    // questionnaire has its own special token handling
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.CV],
  },
  {
    path: path("profile", PathType.EXACT_OR_SUBPATH),
    key: "profile",
    Component: () => <ProfilePage />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: true,
    requiresProfileInConcern: true,
    preloadLocale: [LocContext.PROFILE, LocContext.CV, LocContext.TYPES],
  },
  {
    path: path("jobs", PathType.EXACT_OR_SUBPATH),
    key: "jobs",
    Component: () => <Jobs />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.JOBS],
    activated: (settings) => !!settings.jobview_enabled,
  },
  {
    path: path("job", PathType.EXACT_OR_SUBPATH),
    key: "job",
    Component: () => <Job />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.JOB],
    activated: (settings) => !!settings.jobview_enabled,
  },
  {
    path: path("apply", PathType.EXACT_OR_SUBPATH),
    key: "apply",
    Component: () => <Apply />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.JOB],
    activated: (settings) => !!settings.jobview_enabled,
  },
  {
    path: path("apply-manually", PathType.EXACT_OR_SUBPATH),
    key: "apply-manually",
    Component: () => <ApplyManually />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.JOB],
    activated: (settings) => !!settings.jobview_enabled,
  },
  {
    path: path("parameterOverride", PathType.EXACT_OR_SUBPATH),
    key: "parameterOverride",
    Component: () => <ParameterOverride />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
  },
  {
    path: path("applicationsent", PathType.EXACT_OR_SUBPATH),
    key: "applicationsent",
    Component: () => <ApplicationSent />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.JOB],
    activated: (settings) => !!settings.jobview_enabled,
  },
  {
    path: path("timesheets", PathType.EXACT_OR_SUBPATH),
    key: "timesheets",
    Component: () => <TimeSheetsOverviewPage />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: true,
    requiresProfileInConcern: true,
    preloadLocale: [LocContext.TIMESHEETS],
    activated: (settings) => !!settings.timesheets_enabled,
  },
  {
    path: path("timesheet", PathType.EXACT_OR_SUBPATH),
    key: "timesheet",
    Component: () => <TimeSheetPage />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: true,
    requiresProfileInConcern: true,
    preloadLocale: [LocContext.TIMESHEETS],
    activated: (settings) => !!settings.timesheets_enabled,
  },
  {
    path: path("pwa", PathType.EXACT_OR_SUBPATH),
    key: "pwa",
    Component: () => <PwaActivation />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [],
    activated: (settings) => !!settings.pwa_enabled,
  },
  {
    path: path("admin", PathType.EXACT_OR_SUBPATH),
    key: "admin",
    Component: () => <AdminSettingsEditor />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.ADMIN],
  },
  {
    path: path("login", PathType.EXACT_OR_SUBPATH),
    key: "login",
    Component: () => <Login />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
  },
  {
    path: path("attach-profile", PathType.EXACT_OR_SUBPATH),
    key: "attach-profile",
    Component: () => <AttachProfile />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.PROFILE],
  },
  {
    path: path("selectprofile", PathType.EXACT_OR_SUBPATH),
    key: "selectprofile",
    Component: () => <SelectProfile />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: true,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.PROFILE],
  },
  {
    path: path("colortest", PathType.EXACT_OR_SUBPATH),
    key: "colortest",
    Component: () => <ColorTest />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
  },
  {
    path: path("texttest", PathType.EXACT_OR_SUBPATH),
    key: "texttest",
    Component: () => <TextTest />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
  },
  {
    path: path("embedstats", PathType.EXACT_OR_SUBPATH),
    key: "embedstats",
    Component: () => <EmbedStats />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
  },
  {
    path: path("useroverview", PathType.EXACT_OR_SUBPATH),
    key: "useroverview",
    Component: () => <UserOverview />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
  },
  {
    path: path("errortest", PathType.EXACT_OR_SUBPATH),
    key: "errortest",
    Component: () => <ErrorTest />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
  },
  {
    path: path("howtodeletemydata", PathType.EXACT_OR_SUBPATH),
    key: "howtodeletemydata",
    Component: () => <HowToDeleteMyData />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.PRIVACY, LocContext.PROFILE],
  },
  {
    path: path("privacystatement", PathType.EXACT_OR_SUBPATH),
    key: "privacystatement",
    Component: () => <PrivacyStatement />,
    requiresConcern: true,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.PRIVACY, LocContext.GENERAL],
  },
  {
    path: path("created", PathType.EXACT_OR_SUBPATH),
    key: "created",
    Component: () => <AccountCreatedNoConern />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [LocContext.GENERAL],
  },
  {
    path: path("candidateimpersonation", PathType.EXACT_OR_SUBPATH),
    key: "candidateimpersonation",
    Component: () => <CandidateImpersonation />,
    requiresConcern: false,
    requiresVismaConnectLoggedIn: false,
    requiresProfileInConcern: false,
    preloadLocale: [],
  },
];

export function doesPathRequireLogin(path: string) {
  return !!allRoutes.find((r) => wsRouteMatch(r.path, path))
    ?.requiresVismaConnectLoggedIn;
}

interface Route {
  path: IWsRoutePath;
  key: string;
  Component: () => JSX.Element;

  /** Consider not importing this lazy to reduce length of waterfall */
  Context?: ({ children }) => JSX.Element;
  requiresConcern: boolean;
  requiresProfileInConcern: boolean;
  requiresVismaConnectLoggedIn: boolean;
  preloadLocale?: LocContext[];
  activated?: (settings: CvpSettings) => boolean;
}

interface Result {
  route: Route;
  match: boolean;
  activated: boolean;

  // route requires user to be logged in to visma connect, but is not
  missingLogin?: boolean;

  // route requires concern (k) but we don't have it
  missingConcern?: boolean;

  // route requires profile in concern, but we don't have it
  missingProfileInConcern?: boolean;
}

const useFallbackComponent = (result: Result[]) => {
  const { confirmedLoggedIn, confirmedNotLoggedIn, whoAmI } = useWhoAmI();
  const hasConcern = !!window.cvpIdConcern;
  const numProfiles = whoAmI.numProfiles;

  // fallbacks for /login page when user is already logged in

  const isLoginPage = result.find((r) => r.match && r.route.key === "login");

  if (isLoginPage && confirmedLoggedIn && hasConcern) {
    console.log(
      "isLoginPage && confirmedLoggedIn && hasConcern, Fallback to <FrontPage />"
    );
    return <FrontPage />;
  }

  if (isLoginPage && confirmedLoggedIn && !hasConcern) {
    console.log(
      "isLoginPage && confirmedLoggedIn && !hasConcern, Fallback to <SelectProfile />"
    );
    return <SelectProfile />;
  }

  //

  const nMatched = result.filter((c) => c.match).length;

  const allMatchedAreDeactivated =
    result.filter((c) => c.match && !c.activated).length === nMatched;

  const nMatchesMissingLogin = result.filter(
    (c) => c.match && c.missingLogin
  ).length;

  const nMatchesMissingConcern = result.filter(
    (c) => c.match && c.missingConcern
  ).length;

  const nMatchesMissingProfileInConcern = result.filter(
    (c) => c.match && c.missingProfileInConcern
  ).length;

  const needToLogIn = nMatchesMissingLogin > 0;
  const needConcern = nMatchesMissingConcern > 0;
  const needProfileInConcern = nMatchesMissingProfileInConcern > 0;

  if (nMatched === 0) {
    console.log("nMatched === 0, Fallback to <NothingHere />");
    return <NothingHere />;
  }

  if (allMatchedAreDeactivated) {
    console.log(
      "allMatchedAreDeactivated, Fallback to <NothingHere notActivated />"
    );
    return <NothingHere notActivated />;
  }

  if (needToLogIn) {
    console.log("needToLogIn, Fallback to <Login />");
    return <Login />;
  }

  // if we need consern, but user is not logged in to visma-connect
  // then we can not show <SelectProfile /> because it requires a user to be logged in
  if (needConcern && confirmedNotLoggedIn) {
    console.log("needConcern && confirmedNotLoggedIn, Fallback to <Login />");
    return <Login />;
  }

  // user is logged in but we need concern
  if (needConcern && confirmedLoggedIn) {
    console.log(
      "needConcern && confirmedLoggedIn, Fallback to <SelectProfile />"
    );
    return <SelectProfile />;
  }

  // user is logged in and we have a concern. Page requires profile in current concern, but we dont have it
  if (needProfileInConcern) {
    console.log("needProfileInConcern, Fallback to <CreateProfilePage />");
    return <CreateProfilePage />;
  }

  return null;
};
